import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PO_TYPES, PO_LINE_TYPES, PO_UNITS, PO_IVA_RATES, money } from "@/lib/constants";
import { ArrowLeft, Plus, Trash2, Package, Wrench, Save, Send } from "lucide-react";

const EMPTY_LINE = {
  line_type: "inventariable",
  part_id: null,
  description: "",
  quantity: 1,
  unit: "pza",
  unit_cost: 0,
  iva_rate: 0.16,
  notes: "",
};

function makeLine(partial) {
  return { ...EMPTY_LINE, ...partial };
}

function lineTotals(l) {
  const qty = Number(l.quantity) || 0;
  const cost = Number(l.unit_cost) || 0;
  const iva = Number(l.iva_rate) || 0;
  const subtotal = qty * cost;
  const iva_amount = subtotal * iva;
  const line_total = subtotal + iva_amount;
  return { subtotal, iva_amount, line_total };
}

export default function CompraNueva() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches, activeBranch } = useTenant();
  const navigate = useNavigate();
  const { poId } = useParams();
  const isEdit = !!poId;
  const canCreate = hasPermission("purchase_orders:create");
  const canEditPerm = hasPermission("purchase_orders:edit");
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [loadingPo, setLoadingPo] = useState(isEdit);

  const [header, setHeader] = useState({
    supplier_id: "",
    branch_id: activeBranch?.id || "",
    po_type: "inventariable",
    expected_date: "",
    purpose: "",
    notes: "",
  });
  const [lines, setLines] = useState([makeLine({})]);
  const [saving, setSaving] = useState(false);
  const [existingStatus, setExistingStatus] = useState(null);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  useEffect(() => {
    (async () => {
      if (!canQuery) { setLoadingCatalogs(false); return; }
      setLoadingCatalogs(true);
      try {
        const [{ data: sup }, { data: p }] = await Promise.all([
          api.get("/suppliers", { params: { ...tenantParam, include_inactive: false } }),
          api.get("/parts", { params: { ...tenantParam } }),
        ]);
        setSuppliers(sup);
        setParts(p);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoadingCatalogs(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, isSuperAdmin]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoadingPo(true);
      try {
        const { data } = await api.get(`/purchase-orders/${poId}`);
        setHeader({
          supplier_id: data.supplier_id,
          branch_id: data.branch_id,
          po_type: data.po_type,
          expected_date: data.expected_date || "",
          purpose: data.purpose || "",
          notes: data.notes || "",
        });
        setLines((data.lines || []).map((l) => makeLine({
          line_type: l.line_type,
          part_id: l.part_id,
          description: l.description,
          quantity: l.quantity,
          unit: l.unit,
          unit_cost: l.unit_cost,
          iva_rate: l.iva_rate,
          notes: l.notes || "",
        })));
        setExistingStatus(data.status);
        if (!["borrador", "por_aprobar"].includes(data.status)) {
          toast.error(`No se puede editar una OC en estatus ${data.status}`);
          navigate(`/compras/${poId}`);
        }
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoadingPo(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  useEffect(() => {
    if (!header.branch_id && activeBranch?.id) {
      setHeader((h) => ({ ...h, branch_id: activeBranch.id }));
    }
  }, [activeBranch?.id, header.branch_id]);

  const totals = useMemo(() => {
    let subtotal = 0, iva_amount = 0, total = 0;
    for (const l of lines) {
      const t = lineTotals(l);
      subtotal += t.subtotal;
      iva_amount += t.iva_amount;
      total += t.line_total;
    }
    return { subtotal, iva_amount, total };
  }, [lines]);

  function addLine() {
    // For "servicio" PO, default new line to servicio
    const defaultType = header.po_type === "servicio" ? "servicio" : "inventariable";
    setLines((prev) => [
      ...prev,
      makeLine({
        line_type: defaultType,
        unit: defaultType === "servicio" ? "servicio" : "pza",
      }),
    ]);
  }
  function removeLine(idx) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateLine(idx, patch) {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }
  function onPickPart(idx, partId) {
    const p = parts.find((x) => x.id === partId);
    if (!p) {
      updateLine(idx, { part_id: null });
      return;
    }
    updateLine(idx, {
      part_id: p.id,
      description: p.name,
      unit: p.unit || "pza",
      unit_cost: Number(p.unit_cost || 0),
    });
  }

  function onPoTypeChange(v) {
    setHeader((h) => ({ ...h, po_type: v }));
    // Coerce line types if switching to pure inventariable / servicio
    if (v === "inventariable") {
      setLines((prev) => prev.map((l) => l.line_type === "servicio"
        ? { ...l, line_type: "inventariable", unit: l.unit === "servicio" ? "pza" : l.unit }
        : l,
      ));
    } else if (v === "servicio") {
      setLines((prev) => prev.map((l) => l.line_type === "inventariable"
        ? { ...l, line_type: "servicio", unit: "servicio", part_id: null }
        : l,
      ));
    }
  }

  function validate() {
    if (!header.supplier_id) return "Selecciona un proveedor";
    if (!header.branch_id) return "Selecciona la sucursal";
    if (!lines.length) return "La OC debe tener al menos una partida";
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description?.trim()) return `Partida ${i + 1}: descripción requerida`;
      if (Number(l.quantity) <= 0) return `Partida ${i + 1}: cantidad debe ser mayor a cero`;
      if (Number(l.unit_cost) < 0) return `Partida ${i + 1}: costo no puede ser negativo`;
      if (header.po_type === "inventariable" && l.line_type === "servicio") {
        return `Partida ${i + 1}: OC inventariable no admite partidas de servicio`;
      }
      if (header.po_type === "servicio" && l.line_type === "inventariable") {
        return `Partida ${i + 1}: OC de servicio no admite partidas inventariables`;
      }
    }
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        supplier_id: header.supplier_id,
        branch_id: header.branch_id,
        po_type: header.po_type,
        expected_date: header.expected_date || null,
        purpose: header.purpose || null,
        notes: header.notes || null,
        currency: "MXN",
        lines: lines.map((l) => ({
          line_type: l.line_type,
          part_id: l.line_type === "inventariable" ? (l.part_id || null) : null,
          description: l.description.trim(),
          quantity: Number(l.quantity),
          unit: l.unit,
          unit_cost: Number(l.unit_cost),
          iva_rate: Number(l.iva_rate),
          notes: l.notes || null,
        })),
      };
      if (isEdit) {
        const { data } = await api.patch(`/purchase-orders/${poId}`, payload);
        toast.success(`OC actualizada · ${data.folio}`);
        navigate(`/compras/${data.id}`);
      } else {
        const { data } = await api.post("/purchase-orders", payload, { params: tenantParam });
        toast.success(`OC creada · ${data.folio}${data.status === "aprobada" ? " (auto-aprobada)" : ""}`);
        navigate(`/compras/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canCreate && !isEdit) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No tienes permisos para crear órdenes de compra.
      </div>
    );
  }
  if (isEdit && !canEditPerm) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No tienes permisos para editar órdenes de compra.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-compra-nueva">
      <Button variant="ghost" size="sm" onClick={() => navigate(isEdit ? `/compras/${poId}` : "/compras")} data-testid="po-new-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> {isEdit ? "Volver al detalle" : "Volver a órdenes de compra"}
      </Button>

      <PageHeader
        title={isEdit ? "Editar orden de compra" : "Nueva orden de compra"}
        description={isEdit
          ? `Editando OC en estatus ${existingStatus || "…"}. Los cambios de partidas recalculan totales.`
          : "Registra los datos del proveedor y las partidas de la compra."}
        actions={
          <Button className="btn-tenant" onClick={save} disabled={saving} data-testid="po-new-save">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Guardar OC")}
          </Button>
        }
      />

      {(loadingCatalogs || loadingPo) ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          {/* Header card */}
          <Card className="border-border">
            <CardHeader className="border-b border-border pb-3">
              <CardTitle className="text-base">Cabecera</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Proveedor *</Label>
                <Select
                  value={header.supplier_id}
                  onValueChange={(v) => setHeader({ ...header, supplier_id: v })}
                >
                  <SelectTrigger data-testid="po-h-supplier"><SelectValue placeholder="Selecciona proveedor…" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">
                        No hay proveedores activos. Crea uno en <span className="mono">/proveedores</span>.
                      </div>
                    ) : suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.trade_name || s.legal_name}
                        {s.rfc ? ` · ${s.rfc}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sucursal *</Label>
                <Select
                  value={header.branch_id}
                  onValueChange={(v) => setHeader({ ...header, branch_id: v })}
                >
                  <SelectTrigger data-testid="po-h-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tipo de OC *</Label>
                <Select value={header.po_type} onValueChange={onPoTypeChange}>
                  <SelectTrigger data-testid="po-h-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PO_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha esperada</Label>
                <Input
                  type="date"
                  value={header.expected_date}
                  onChange={(e) => setHeader({ ...header, expected_date: e.target.value })}
                  data-testid="po-h-expected"
                />
              </div>
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Input value="MXN" readOnly className="bg-slate-50" data-testid="po-h-currency" />
              </div>

              <div className="space-y-2 md:col-span-3">
                <Label>Motivo / Uso de compra</Label>
                <Input
                  value={header.purpose}
                  onChange={(e) => setHeader({ ...header, purpose: e.target.value })}
                  placeholder="Ej. Reposición de stock, contratación de servicio, etc."
                  data-testid="po-h-purpose"
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Notas / Observaciones</Label>
                <Textarea
                  rows={2}
                  value={header.notes}
                  onChange={(e) => setHeader({ ...header, notes: e.target.value })}
                  data-testid="po-h-notes"
                />
              </div>
            </CardContent>
          </Card>

          {/* Lines card */}
          <Card className="border-border">
            <CardHeader className="border-b border-border pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Partidas</CardTitle>
                <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line">
                  <Plus className="h-4 w-4 mr-1" /> Agregar partida
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {lines.map((l, idx) => {
                const t = lineTotals(l);
                const canPickPart = header.po_type !== "servicio" && l.line_type === "inventariable";
                return (
                  <div
                    key={idx}
                    className="border border-border rounded-md p-3 space-y-3 bg-slate-50/40"
                    data-testid={`po-line-${idx}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-muted-foreground">Partida {idx + 1}</span>
                      {header.po_type === "mixta" && (
                        <Select
                          value={l.line_type}
                          onValueChange={(v) => updateLine(idx, {
                            line_type: v,
                            part_id: v === "servicio" ? null : l.part_id,
                            unit: v === "servicio" ? "servicio" : (l.unit === "servicio" ? "pza" : l.unit),
                          })}
                        >
                          <SelectTrigger className="h-8 w-40" data-testid={`po-line-${idx}-type`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PO_LINE_TYPES.map((lt) => (
                              <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {header.po_type !== "mixta" && (
                        <span className="text-[10px] uppercase text-slate-600 inline-flex items-center gap-1">
                          {l.line_type === "servicio"
                            ? <><Wrench className="h-3 w-3" /> servicio</>
                            : <><Package className="h-3 w-3" /> inventariable</>}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-red-600 hover:text-red-700"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                        data-testid={`po-line-${idx}-delete`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {canPickPart && parts.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          Refacción del catálogo (opcional)
                        </Label>
                        <Select value={l.part_id || "none"} onValueChange={(v) => onPickPart(idx, v === "none" ? null : v)}>
                          <SelectTrigger data-testid={`po-line-${idx}-part`}>
                            <SelectValue placeholder="Sin vincular" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Sin vincular a catálogo —</SelectItem>
                            {parts.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="space-y-2 md:col-span-5">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Descripción *</Label>
                        <Input
                          value={l.description}
                          onChange={(e) => updateLine(idx, { description: e.target.value })}
                          placeholder={l.line_type === "servicio"
                            ? "Ej. Instalación de sistema eléctrico"
                            : "Ej. Filtro de aceite Nissan Sentra 2019"}
                          data-testid={`po-line-${idx}-desc`}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cant. *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={l.quantity}
                          onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                          data-testid={`po-line-${idx}-qty`}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Unidad</Label>
                        <Select value={l.unit} onValueChange={(v) => updateLine(idx, { unit: v })}>
                          <SelectTrigger data-testid={`po-line-${idx}-unit`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PO_UNITS.map((u) => (
                              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-3">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Costo unitario *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={l.unit_cost}
                          onChange={(e) => updateLine(idx, { unit_cost: e.target.value })}
                          data-testid={`po-line-${idx}-cost`}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">IVA</Label>
                        <Select
                          value={String(l.iva_rate)}
                          onValueChange={(v) => updateLine(idx, { iva_rate: Number(v) })}
                        >
                          <SelectTrigger data-testid={`po-line-${idx}-iva`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PO_IVA_RATES.map((r) => (
                              <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-10 md:col-start-3 flex items-end justify-end text-xs text-muted-foreground">
                        <span className="mono">
                          Subtotal <b>{money(t.subtotal)}</b> · IVA <b>{money(t.iva_amount)}</b> · Total {" "}
                          <b className="text-slate-800">{money(t.line_total)}</b>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-end pt-2 border-t border-border">
                <div className="text-right text-sm space-y-1" data-testid="po-totals">
                  <div>Subtotal: <span className="mono ml-2">{money(totals.subtotal)}</span></div>
                  <div>IVA: <span className="mono ml-2">{money(totals.iva_amount)}</span></div>
                  <div className="text-base font-semibold">Total: <span className="mono ml-2">{money(totals.total)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 pb-8">
            <Button variant="outline" onClick={() => navigate(isEdit ? `/compras/${poId}` : "/compras")}>Cancelar</Button>
            <Button className="btn-tenant" onClick={save} disabled={saving} data-testid="po-new-save-bottom">
              <Send className="h-4 w-4 mr-2" />
              {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Guardar OC")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
